import {useCallback, useEffect, useId, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent} from 'react'
import {AnchoredOverlay} from '@primer/react'
import {RepoIcon} from '@primer/octicons-react'
import {useHoverPopover} from '@github-ui/use-hover-popover'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useNavigate, useSearchParams} from '@github-ui/use-navigate'
import type {SidebarChat} from '../../../types/sidebar-chat'
import {useSidebarHovercardExclusivity} from '@github-ui/copilot-shared-ui/hooks/use-sidebar-item-hovercard'
import type {UITask} from '../../../types/task'
import {NO_REPOSITORY_GROUP} from '../../../constants/grouping'
import {
  useGroupedTasks,
  type GroupableItem,
  type SidebarGroup,
  type TaskRepoGroup,
} from '../../../hooks/use-grouped-tasks'
import {generateTaskUrlForRepo, shouldUseGlobalTaskUrls} from '../../../utils/generate-task-path'
import {getAgentsPath} from '../../../utils/constants'
import {computeRepoGroupLabels, repoDisplayNameWithOwner, repoShortName} from '../../../utils/repo-group-label'
import {useRepoInfo} from '../../../hooks/use-repo-info'
import {SessionsNavGroup} from './SessionsNavGroup'
import {SessionsNavGroupHovercard} from './SessionsNavGroupHovercard'
import hovercardStyles from './SessionsNavGroupHovercard.module.css'
import {SessionsNavItem} from './SessionsNavItem'
import {getChatSessionNavItemProps} from '../ChatsNavGroup'
import {getSidebarChatHref, isSidebarChatCurrent} from '../../../hooks/use-legacy-sidebar-chats'
import {SessionsNavListShowAction} from './SessionsNavListShowAction'

const INITIAL_TASK_COUNT = 10

/** `null` means the repository policy request is still pending. */
export type RepositoryAgentEnabledById = Record<string, boolean | null>

/** Normalizes bare tasks (Repository mode) and already-tagged items (Status/Last-updated
 * mode, which mix in chats) into one shape so rendering doesn't need to branch. */
function toGroupableItems(entries: ReadonlyArray<UITask | GroupableItem>): GroupableItem[] {
  return entries.map(entry => ('kind' in entry ? entry : {kind: 'task', task: entry}))
}

// A fraction of the hook's 200ms hover-open delay. Dwelling this long on a repo
// heading warms the repository-info request before the card opens, so it opens
// already populated instead of flashing a loading shimmer. Buys ~125ms.
const HOVER_PREFETCH_DELAY_MS = 75

export interface SessionsByRepoNavProps {
  /** Flat task list to group client-side. Ignored when `groups` is provided. */
  tasks?: readonly UITask[]
  /** Pre-computed group data; if provided, used as-is instead of re-deriving groups from `tasks`. */
  groups?: readonly TaskRepoGroup[] | readonly SidebarGroup[]
  /** ID of the task currently being viewed; its row renders as the current page. */
  activeTaskId?: string
  /** When true, new-session actions navigate back to the global Agents page instead of updating the current URL. */
  navigateToAgentsPage?: boolean
  activeChatTaskId?: string
  activeThreadId?: string
  chatApiURL?: string
  /** Called after a repository's new-session button is activated. */
  onNewSession?: () => void
  repositoryAgentEnabledById?: RepositoryAgentEnabledById
  getChatStatusLabel?: (chat: SidebarChat) => string
  onChatNavigate?: (href: string, event: ReactMouseEvent<HTMLElement>) => void
  onLegacyChatNavigate?: (href: string, event: ReactMouseEvent<HTMLElement>) => void
}

function firstTaskRepositoryName(items: readonly GroupableItem[]): string | undefined {
  for (const item of items) {
    if (item.kind === 'task' && item.task.repositoryName) return item.task.repositoryName
  }
  return undefined
}

interface RenderableItemsContext {
  repoNwo?: string
  useGlobalTaskUrls: boolean
  searchParams: URLSearchParams
}

function computeRenderableItems(
  items: readonly GroupableItem[],
  {repoNwo, useGlobalTaskUrls, searchParams}: RenderableItemsContext,
): Array<{item: GroupableItem; href: string}> {
  return items.flatMap<{item: GroupableItem; href: string}>(item => {
    let href: string | undefined

    if (item.kind === 'chat') {
      href = getSidebarChatHref(
        item.chat,
        generateTaskUrlForRepo({
          repositoryNwo: repoNwo,
          taskId: item.chat.taskId,
          searchParams,
        }) ?? '',
        searchParams,
      )
    } else {
      const {task} = item
      const taskSearchParams = new URLSearchParams(searchParams)
      taskSearchParams.delete('session_id')
      const taskRepoNwo = task.nwo ? repoDisplayNameWithOwner(task.nwo) : repoNwo

      href = generateTaskUrlForRepo({
        repositoryNwo: useGlobalTaskUrls ? undefined : taskRepoNwo,
        taskId: task.id,
        sessionId: task.latest_session_id,
        searchParams: taskSearchParams,
      })
    }

    return href ? [{item, href}] : []
  })
}

function groupRepoNwo(groupKey: string): string | undefined {
  return groupKey === NO_REPOSITORY_GROUP ? undefined : repoDisplayNameWithOwner(groupKey)
}

/**
 * Whether any group has at least one item that can produce a valid href.
 * Unlike raw group/task counts, this accounts for items that get dropped at
 * render time because they fail href generation (see `computeRenderableItems`).
 */
export function hasRenderableGroups(
  groups: readonly TaskRepoGroup[] | readonly SidebarGroup[],
  context: {useGlobalTaskUrls: boolean; searchParams: URLSearchParams},
): boolean {
  return groups.some(([groupKey, groupEntries]) => {
    const items = toGroupableItems(groupEntries)
    const repoNwo = groupRepoNwo(groupKey)
    return computeRenderableItems(items, {repoNwo, ...context}).length > 0
  })
}

/**
 * Renders the grouped repo → session list as children of a parent
 * `<NavList>` (or `<NavList.Group>`). Returns a fragment so callers retain
 * full control over the surrounding NavList structure.
 */
export function SessionsByRepoNav({
  tasks,
  groups: serverGroups,
  activeTaskId,
  navigateToAgentsPage,
  activeChatTaskId,
  activeThreadId,
  chatApiURL,
  onNewSession,
  repositoryAgentEnabledById,
  getChatStatusLabel,
  onChatNavigate,
  onLegacyChatNavigate,
}: SessionsByRepoNavProps) {
  const clientGroups = useGroupedTasks(tasks ?? [])
  const groups = serverGroups ?? clientGroups
  const [searchParams] = useSearchParams()
  const useGlobalTaskUrls = shouldUseGlobalTaskUrls({
    taskUrlScope: 'global',
    taskDetailEnabled: isFeatureEnabled('copilot_mission_control_agents_task_detail'),
    repositoryTaskDetailEnabled: isFeatureEnabled('copilot_mission_control_agents_repository_task_detail'),
  })

  const renderableGroups = useMemo(
    () =>
      groups
        .map(([groupKey, groupEntries]) => {
          const items = toGroupableItems(groupEntries)
          const repoNwo = groupRepoNwo(groupKey)
          return {groupKey, items, repoNwo}
        })
        .filter(
          group =>
            computeRenderableItems(group.items, {repoNwo: group.repoNwo, useGlobalTaskUrls, searchParams}).length > 0,
        ),
    [groups, useGlobalTaskUrls, searchParams],
  )

  const labels = useMemo(
    () =>
      computeRepoGroupLabels(
        renderableGroups.map(({groupKey, items}) => ({
          key: groupKey,
          // Only trust `repositoryName` for real owner/repo groups; status/recency groups mix repos.
          sessionSectionHeader: repoShortName(groupKey) ? firstTaskRepositoryName(items) : undefined,
        })),
      ),
    [renderableGroups],
  )

  if (renderableGroups.length === 0) return null

  return (
    <>
      {renderableGroups.map(({groupKey, items}) => (
        <RepoNavGroup
          key={groupKey}
          groupKey={groupKey}
          label={labels.get(groupKey) ?? groupKey}
          items={items}
          activeTaskId={activeTaskId}
          navigateToAgentsPage={navigateToAgentsPage}
          activeChatTaskId={activeChatTaskId}
          activeThreadId={activeThreadId}
          chatApiURL={chatApiURL}
          onNewSession={onNewSession}
          repositoryAgentEnabledById={repositoryAgentEnabledById}
          getChatStatusLabel={getChatStatusLabel}
          onChatNavigate={onChatNavigate}
          onLegacyChatNavigate={onLegacyChatNavigate}
        />
      ))}
    </>
  )
}

interface RepoNavGroupProps {
  groupKey: string
  label: string
  items: readonly GroupableItem[]
  activeTaskId?: string
  navigateToAgentsPage?: boolean
  activeChatTaskId?: string
  activeThreadId?: string
  chatApiURL?: string
  onNewSession?: () => void
  repositoryAgentEnabledById?: RepositoryAgentEnabledById
  getChatStatusLabel?: (chat: SidebarChat) => string
  onChatNavigate?: (href: string, event: ReactMouseEvent<HTMLElement>) => void
  onLegacyChatNavigate?: (href: string, event: ReactMouseEvent<HTMLElement>) => void
}

const RepoNavGroup = ({
  groupKey,
  label,
  items,
  activeTaskId,
  navigateToAgentsPage,
  activeChatTaskId,
  activeThreadId,
  chatApiURL,
  onNewSession,
  repositoryAgentEnabledById,
  getChatStatusLabel,
  onChatNavigate,
  onLegacyChatNavigate,
}: RepoNavGroupProps) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [showAllTasks, setShowAllTasks] = useState(false)
  const [dwelled, setDwelled] = useState(false)
  const popoverId = useId()
  const {open, anchorRegionProps, anchorProps, contentProps, closeNow} = useHoverPopover<HTMLSpanElement>()
  const noReturnFocusRef = useRef<HTMLElement>(null)
  const repoRowRef = useRef<HTMLDivElement>(null)
  const dwellTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const {
    ref: anchorRegionRef,
    onPointerEnter: onRegionPointerEnter,
    onPointerLeave: onRegionPointerLeave,
  } = anchorRegionProps

  const handleRegionPointerEnter = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      onRegionPointerEnter()
      // A tap cannot express hover intent, so prefetching here would turn ordinary sidebar taps into speculative requests.
      // The hover-popover hook still runs above, which preserves its touch behavior.
      if (event.pointerType === 'touch') return
      // Mirror scheduleOpen: if the dwell timer is already armed, don't reset it.
      if (dwellTimeoutRef.current) return
      dwellTimeoutRef.current = setTimeout(() => {
        dwellTimeoutRef.current = null
        setDwelled(true)
      }, HOVER_PREFETCH_DELAY_MS)
    },
    [onRegionPointerEnter],
  )

  const handleRegionPointerLeave = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (dwellTimeoutRef.current) {
        clearTimeout(dwellTimeoutRef.current)
        dwellTimeoutRef.current = null
      }
      setDwelled(false)
      onRegionPointerLeave(event)
    },
    [onRegionPointerLeave],
  )

  useEffect(() => {
    return () => {
      if (dwellTimeoutRef.current) clearTimeout(dwellTimeoutRef.current)
    }
  }, [])

  const composedAnchorRegionProps = useMemo(
    () => ({ref: anchorRegionRef, onPointerEnter: handleRegionPointerEnter, onPointerLeave: handleRegionPointerLeave}),
    [anchorRegionRef, handleRegionPointerEnter, handleRegionPointerLeave],
  )

  const isOrphanGroup = groupKey === NO_REPOSITORY_GROUP
  const repoNwo = groupRepoNwo(groupKey)
  const isRepositoryGroup = repoNwo !== undefined || isOrphanGroup
  // Status/no-status groups don't map to a single repo, so skip the hovercard for them.
  const showHovercard = repoNwo !== undefined
  useSidebarHovercardExclusivity(showHovercard && open, closeNow)
  const useGlobalTaskUrls = shouldUseGlobalTaskUrls({
    taskUrlScope: 'global',
    taskDetailEnabled: isFeatureEnabled('copilot_mission_control_agents_task_detail'),
    repositoryTaskDetailEnabled: isFeatureEnabled('copilot_mission_control_agents_repository_task_detail'),
  })
  // Tasks are grouped by NWO, and hydrated server groups assign one repository ID to every task in the group.
  const repoId =
    isOrphanGroup || repoNwo === undefined
      ? undefined
      : items.find(
          (item): item is {kind: 'task'; task: UITask} => item.kind === 'task' && item.task.repo_id !== undefined,
        )?.task.repo_id
  const requestedRepoIds = useMemo(
    () => ((open || dwelled) && repoId !== undefined ? [repoId] : []),
    [open, dwelled, repoId],
  )
  const {data: repoInfoById, isFetching: isRepoInfoFetching} = useRepoInfo(requestedRepoIds, {
    requireFullDetails: true,
  })
  const repoInfo = repoId === undefined ? undefined : repoInfoById[repoId]
  const renderableItems = computeRenderableItems(items, {repoNwo, useGlobalTaskUrls, searchParams})
  const hasMoreTasks = renderableItems.length > INITIAL_TASK_COUNT
  const visibleItems = showAllTasks ? renderableItems : renderableItems.slice(0, INITIAL_TASK_COUNT)
  const latestSessionUpdatedAt = renderableItems.reduce<string | undefined>((latest, {item}) => {
    const timestamp = item.kind === 'task' ? item.task.latest_session_updated_at : item.chat.updatedAt
    if (!timestamp || Number.isNaN(Date.parse(timestamp))) return latest
    if (!latest || Date.parse(timestamp) > Date.parse(latest)) return timestamp
    return latest
  }, undefined)
  const newSessionHref = repoNwo
    ? `${getAgentsPath()}?${new URLSearchParams({repo: repoNwo, new_session: '1'}).toString()}`
    : undefined
  const repositoryAgentEnabled = repoId === undefined ? undefined : repositoryAgentEnabledById?.[repoId.toString()]
  const newSessionPending = repositoryAgentEnabled === null
  const newSessionDisabled = repositoryAgentEnabled === false
  const handleAnchorFocus = useCallback(
    (event: React.FocusEvent<HTMLButtonElement>) => {
      if (event.currentTarget.matches(':focus-visible')) anchorProps.onFocus()
    },
    [anchorProps],
  )
  const handleNewSessionClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!repoNwo) return
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
    event.preventDefault()
    event.stopPropagation()
    if (navigateToAgentsPage && newSessionHref) {
      navigate(newSessionHref)
    } else {
      setSearchParams(new URLSearchParams({repo: repoNwo, new_session: '1'}))
    }
    onNewSession?.()
  }
  const handleTaskClick = useCallback(
    (href: string, event: React.MouseEvent<HTMLAnchorElement>) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Preserve native modified-link behavior.
      const hasModifierKey = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
      if (event.defaultPrevented || event.button !== 0 || hasModifierKey) return

      event.preventDefault()
      navigate(href)
    },
    [navigate],
  )

  return (
    <SessionsNavGroup
      groupKey={groupKey}
      label={label}
      icon={isRepositoryGroup ? RepoIcon : undefined}
      // Pending actions are hidden; disabled actions render as inactive buttons.
      newSessionHref={newSessionDisabled || newSessionPending ? undefined : newSessionHref}
      newSessionDisabled={newSessionDisabled}
      onNewSessionClick={handleNewSessionClick}
      repoNwo={repoNwo}
      rowRef={repoRowRef}
      onToggle={showHovercard ? closeNow : undefined}
      anchorRegionProps={showHovercard ? composedAnchorRegionProps : undefined}
      anchorButtonProps={
        showHovercard
          ? {
              onFocus: handleAnchorFocus,
              onBlur: anchorProps.onBlur,
              'aria-describedby': open ? popoverId : undefined,
            }
          : undefined
      }
      hovercard={
        showHovercard &&
        open && (
          <AnchoredOverlay
            open={open}
            anchorRef={repoRowRef}
            renderAnchor={null}
            onClose={closeNow}
            focusTrapSettings={{disabled: true}}
            focusZoneSettings={{disabled: true}}
            overlayProps={{preventFocusOnOpen: true, returnFocusRef: noReturnFocusRef}}
            side="outside-right"
            align="start"
          >
            <div {...contentProps} id={popoverId} data-testid="repo-hovercard" className={hovercardStyles.hovercard}>
              <SessionsNavGroupHovercard
                repoNwo={repoNwo ?? groupKey}
                sessionCount={renderableItems.length}
                latestSessionUpdatedAt={latestSessionUpdatedAt}
                repoInfo={repoInfo}
                isRepoInfoFetching={isRepoInfoFetching}
              />
            </div>
          </AnchoredOverlay>
        )
      }
    >
      {visibleItems.map(({item, href}) => {
        if (item.kind === 'chat') {
          const {chat} = item
          return (
            <SessionsNavItem
              key={chat.legacy ? `legacy-${chat.taskId}` : chat.taskId}
              {...getChatSessionNavItemProps({
                chat,
                href,
                isCurrent: isSidebarChatCurrent(chat, {activeChatTaskId, activeThreadId}),
                chatApiURL,
                getStatusLabel: getChatStatusLabel,
                onNavigate: chat.legacy ? onLegacyChatNavigate : onChatNavigate,
              })}
            />
          )
        }
        const {task} = item
        const key = task.id ?? task.clientGeneratedId ?? `${groupKey}-${task.resource_id}`
        return (
          <SessionsNavItem
            key={key}
            enablePinning={!task.isOptimistic}
            task={task}
            href={href}
            isSelected={!!activeTaskId && task.id === activeTaskId}
            onClick={event => handleTaskClick(href, event)}
          />
        )
      })}
      {hasMoreTasks && !showAllTasks && (
        <SessionsNavListShowAction
          onClick={() => setShowAllTasks(true)}
          hiddenLabel={`tasks for ${groupKey}`}
          aria-expanded="false"
        >
          Show more...
        </SessionsNavListShowAction>
      )}
    </SessionsNavGroup>
  )
}
